import {
  isMediaCardType,
  mediaCardTypeToKeys,
} from '@wandb/weave/common/types/media';
import {useCallback, useMemo} from 'react';

import {useUniqueKeyTypesQuery} from '../../../../generated/graphql';
import {useNgwTruncatedPanelGenLimits} from '../../../../util/dynamicConfigs/ngwTruncatedPanelGenLimits';
import {propagateErrorsContext} from '../../../../util/errors';
import {toMongo} from '../../../../util/filters';
import * as QueryTS from '../../../../util/queryts';
import {useIsNgwEnabled} from '../../../../util/rampFeatureFlags/rampFeatureFlags';
import {useKeyInfoQueryContext} from '../../../MultiRunWorkspace/KeyInfoQueryContext';
import {useWorkspaceHistoryKeyInfoQueryVars} from '../../../MultiRunWorkspace/useWorkspaceHistoryKeyInfoQueryVars';
import {type PanelType} from '../types';

const NUMBER_TYPES: PanelType[] = [
  'line',
  'scatter',
  'parallel',
  'bar',
  'scalar',
];
const TABLE_FILE_TYPES: PanelType[] = [
  'weave-table',
  'weave-plot',
  'weave-projector',
];
// The following types refer to the new generation of weave panels which include weave-trace, weave-evaluations, etc.
const WEAVE_TYPES: PanelType[] = ['weave-trace'];

const ALWAYS_ENABLED: Set<PanelType> = new Set([
  'code',
  'comparer',
  'markdown',
  'panel-weave',
  'parallel',
  'parameter-importance',
  'quick-add',
  'scatter',
  'vega2',
  'marimo',
  ...WEAVE_TYPES,
]);

export function isPanelTypeAlwaysEnabled(type: PanelType): boolean {
  return ALWAYS_ENABLED.has(type);
}

function isPanelTypeEnabledForKeyTypes(
  type: PanelType,
  uniqueKeyTypes: Set<string>
): boolean {
  if (NUMBER_TYPES.includes(type)) {
    return uniqueKeyTypes.has('number');
  }

  if (TABLE_FILE_TYPES.includes(type)) {
    return uniqueKeyTypes.has('table-file');
  }

  if (isMediaCardType(type)) {
    const keys = mediaCardTypeToKeys(type);
    for (const key of keys) {
      if (uniqueKeyTypes.has(key)) {
        return true;
      }
    }
  }

  return uniqueKeyTypes.has(type);
}

export const useIsPanelTypeEnabled: () => {
  checkPanelEnabled: (type: PanelType) => boolean;
  retry: () => void;
  isLoading: boolean;
  isError: boolean;
} = () => {
  const isNextGenAddPanelsDrawer = useIsNgwEnabled();
  const {uniqueKeyTypes: legacyKeyTypes} = useKeyInfoQueryContext();
  const {
    config: {runLimit},
  } = useNgwTruncatedPanelGenLimits();
  const {
    entityName,
    projectName,
    filters,
    sort,
    requiredRunKeys = [],
  } = useWorkspaceHistoryKeyInfoQueryVars();
  const nextGenQuery = useUniqueKeyTypesQuery({
    variables: {
      entityName,
      projectName,
      filters: JSON.stringify(toMongo(filters)),
      runLimit,
      runOrder: QueryTS.sortToOrderString(sort),
      requiredRunKeys: requiredRunKeys.length > 0 ? requiredRunKeys : undefined,
    },
    // Invalid workspace filters are user-correctable. Keep the rejection on
    // the query result so AddPanels can show its availability warning.
    context: propagateErrorsContext(),
    skip: !isNextGenAddPanelsDrawer,
  });

  const uniqueKeyTypes = useMemo(() => {
    if (isNextGenAddPanelsDrawer) {
      return nextGenQuery.data?.project?.runs?.uniqueKeyTypes ?? [];
    }
    return legacyKeyTypes;
  }, [isNextGenAddPanelsDrawer, nextGenQuery.data, legacyKeyTypes]);
  const isLoading = isNextGenAddPanelsDrawer && nextGenQuery.loading;
  const isError = isNextGenAddPanelsDrawer && !!nextGenQuery.error;
  const {refetch} = nextGenQuery;
  const retry = useCallback(() => {
    if (isNextGenAddPanelsDrawer) {
      refetch().catch(() => undefined);
    }
  }, [isNextGenAddPanelsDrawer, refetch]);

  const checkPanelEnabled = useCallback(
    (type: PanelType): boolean => {
      if (isPanelTypeAlwaysEnabled(type)) {
        return true;
      }

      return (
        !isLoading &&
        !isError &&
        uniqueKeyTypes != null &&
        isPanelTypeEnabledForKeyTypes(type, new Set(uniqueKeyTypes))
      );
    },
    [uniqueKeyTypes, isLoading, isError]
  );

  return useMemo(
    () => ({checkPanelEnabled, retry, isLoading, isError}),
    [checkPanelEnabled, retry, isLoading, isError]
  );
};
